import React, {
  FC,
  memo,
  RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MatchStatus } from "service";
import { SIDE } from "_constants";
import { getTeamColors } from "components/colors";
import ResizeObserver from "resize-observer-polyfill";
import {
  isShotEventCode,
  isThreePointCode,
  isTwoPointCode,
} from "../constants";

type Props = {
  status: MatchStatus;
  eventRunId: number;
  onStart?: () => void;
  onEnd?: () => void;
  courtRef: RefObject<HTMLElement | null>;
  leftHoopRef: RefObject<HTMLElement | null>;
  rightHoopRef: RefObject<HTMLElement | null>;
  ballRef?: RefObject<SVGImageElement | HTMLImageElement | null>;
};

const DURATIONS: Record<string, number> = {
  "1074": 960, // 2P (legacy)
  "1095": 960, // 2P
  "1075": 960, // 3P (legacy)
  "1094": 960, // 3P
  "1079": 960, // FT Scored
};

const SHOT_PATH_FADE_OUT_DELAY_MS = 600;

function clamp01(n: number) {
  return Math.max(0, Math.min(1, n));
}

type Layout = {
  viewW: number;
  viewH: number;
  start: { x: number; y: number };
  end: { x: number; y: number };
};

export const ShotPath: FC<Props> = memo(
  ({
    status,
    eventRunId,
    onStart,
    onEnd,
    courtRef,
    leftHoopRef,
    rightHoopRef,
    ballRef,
  }) => {
    const nsc = String((status as any)?.nsc ?? "");
    const side = (status as any)?.side;
    const onStartRef = useRef(onStart);
    const onEndRef = useRef(onEnd);

    useEffect(() => {
      onStartRef.current = onStart;
    }, [onStart]);

    useEffect(() => {
      onEndRef.current = onEnd;
    }, [onEnd]);

    // Start position must always match the ball's displayed position
    const rawX = (status as any)?.x ?? (status as any)?.X;
    const rawY = (status as any)?.y ?? (status as any)?.Y;
    const isNum = (v: any) => typeof v === "number" && Number.isFinite(v);

    let sxRaw: number | undefined = isNum(rawX) ? Number(rawX) : undefined;
    let syRaw: number | undefined = isNum(rawY) ? Number(rawY) : undefined;

    if (sxRaw === undefined || syRaw === undefined) {
      const isHome = String(side) === String(SIDE.HOME);
      if (nsc === "1079") {
        // Free Throw: fixed fallback (same as Ball)
        sxRaw = isHome ? 0.77 : 0.23;
        syRaw = 0.5;
      } else if (isTwoPointCode(nsc) || isThreePointCode(nsc)) {
        // 2P/3P: fixed fallback like Free Throw (same as Ball)
        if (isTwoPointCode(nsc)) {
          sxRaw = isHome ? 0.7 : 0.3;
          syRaw = 0.5;
        } else {
          sxRaw = isHome ? 0.65 : 0.35;
          syRaw = 0.5;
        }
      }
    }

    // Persist last known start to avoid sudden jumps
    const lastStartRef = useRef<{ x: number; y: number }>({ x: 0.5, y: 0.5 });
    if (sxRaw !== undefined && syRaw !== undefined) {
      lastStartRef.current = { x: clamp01(sxRaw), y: clamp01(syRaw) };
    }
    const sx = lastStartRef.current.x;
    const sy = lastStartRef.current.y;

    const { home, away } = useMemo(
      () => getTeamColors(status as any),
      [status]
    );
    const strokeColor = useMemo(
      () => (String(side) === String(SIDE.HOME) ? home : away),
      [home, away, side]
    );

    const isShotEvent = isShotEventCode(nsc); // show FT path only when scored
    const duration = DURATIONS[nsc] ?? 0;

    const targetHoopRef = useMemo(
      () => (String(side) === String(SIDE.HOME) ? rightHoopRef : leftHoopRef),
      [leftHoopRef, rightHoopRef, side]
    );

    const shotEventKey = useMemo(() => {
      const s = status as any;
      const payload = {
        runId: eventRunId,
        nsc,
        tm: s?.tm ?? null,
        pc: s?.pc ?? null,
        ta: s?.ta ?? null,
        stn: s?.stn ?? null,
        side: s?.side ?? null,
        seq: s?.c?.seq ?? null,
        id: s?.c?.id ?? s?.c?.eid ?? s?.c?.rid ?? null,
        ts: s?.c?.ts ?? s?.c?.timestamp ?? null,
        x: isNum(s?.x) ? Number(s?.x) : sxRaw,
        y: isNum(s?.y) ? Number(s?.y) : syRaw,
        xy: typeof s?.xy === "string" ? s.xy : null,
      };
      return JSON.stringify(payload);
    }, [eventRunId, status, nsc, sxRaw, syRaw]);

    const [layout, setLayout] = useState<Layout | null>(null);
    const [visible, setVisible] = useState(false);
    const roRef = useRef<ResizeObserver | null>(null);

    const ballElement = ballRef?.current ?? null;

    useLayoutEffect(() => {
      if (!isShotEvent) {
        setLayout(null);
        return;
      }

      const courtEl = courtRef.current;
      const hoopEl = targetHoopRef?.current;
      if (!courtEl || !hoopEl) {
        setLayout(null);
        return;
      }

      const overlayEl = courtEl.querySelector(
        ".overlay-container"
      ) as HTMLElement | null;

      const computeLayout = () => {
        if (!courtEl || !hoopEl) {
          setLayout(null);
          return;
        }

        const courtRect = courtEl.getBoundingClientRect();
        const overlayRect = overlayEl?.getBoundingClientRect() ?? null;
        const hoopRect = hoopEl.getBoundingClientRect();

        const baseLeft = courtRect.left;
        const baseTop = courtRect.top;
        const overlayLeft = overlayRect?.left ?? baseLeft;
        const overlayTop = overlayRect?.top ?? baseTop;
        const overlayWidth = overlayRect?.width ?? courtRect.width;
        const overlayHeight = overlayRect?.height ?? courtRect.height;

        const projectOverlayPoint = (
          normX: number,
          normY: number
        ): { x: number; y: number } | null => {
          if (!overlayEl) return null;
          const marker = document.createElement("div");
          marker.style.position = "absolute";
          marker.style.pointerEvents = "none";
          marker.style.width = "0px";
          marker.style.height = "0px";
          marker.style.left = `${normX * 100}%`;
          marker.style.top = `${normY * 100}%`;
          marker.style.visibility = "hidden";
          overlayEl.appendChild(marker);
          const markerRect = marker.getBoundingClientRect();
          overlayEl.removeChild(marker);
          if (
            !markerRect ||
            Number.isNaN(markerRect.left) ||
            Number.isNaN(markerRect.top)
          ) {
            return null;
          }
          return {
            x: markerRect.left - baseLeft,
            y: markerRect.top - baseTop,
          };
        };

        let startPoint: { x: number; y: number } | null = null;

        if (overlayEl) {
          startPoint = projectOverlayPoint(sx, sy);
        }

        if (!startPoint && ballElement) {
          const ballRect = ballElement.getBoundingClientRect();
          if (ballRect.width > 0 && ballRect.height > 0) {
            startPoint = {
              x: ballRect.left + ballRect.width / 2 - baseLeft,
              y: ballRect.top + ballRect.height / 2 - baseTop,
            };
          }
        }

        let startX: number | null = startPoint?.x ?? null;
        let startY: number | null = startPoint?.y ?? null;

        if (
          startX === null ||
          startY === null ||
          Number.isNaN(startX) ||
          Number.isNaN(startY)
        ) {
          startX = overlayLeft + sx * overlayWidth - baseLeft;
          startY = overlayTop + sy * overlayHeight - baseTop;
        }

        if (
          startX === null ||
          startY === null ||
          Number.isNaN(startX) ||
          Number.isNaN(startY)
        ) {
          setLayout(null);
          return;
        }

        const endX = hoopRect.left + hoopRect.width / 2 - baseLeft;
        const endY = hoopRect.top + hoopRect.height * 0.2 - baseTop;

        const nextLayout: Layout = {
          viewW: courtRect.width,
          viewH: courtRect.height,
          start: { x: startX, y: startY },
          end: { x: endX, y: endY },
        };

        setLayout((prev) => {
          if (prev) {
            const unchanged =
              Math.abs(prev.viewW - nextLayout.viewW) < 0.25 &&
              Math.abs(prev.viewH - nextLayout.viewH) < 0.25 &&
              Math.abs(prev.start.x - nextLayout.start.x) < 0.25 &&
              Math.abs(prev.start.y - nextLayout.start.y) < 0.25 &&
              Math.abs(prev.end.x - nextLayout.end.x) < 0.25 &&
              Math.abs(prev.end.y - nextLayout.end.y) < 0.25;
            if (unchanged) return prev;
          }
          return nextLayout;
        });
      };

      computeLayout();
      const recalcTimeouts: number[] = [];
      const scheduleRecalc = (delay: number) => {
        const id = window.setTimeout(() => {
          computeLayout();
          const idx = recalcTimeouts.indexOf(id);
          if (idx >= 0) recalcTimeouts.splice(idx, 1);
        }, delay);
        recalcTimeouts.push(id);
      };
      scheduleRecalc(90);
      scheduleRecalc(180);
      scheduleRecalc(320);

      const resizeObserver =
        roRef.current ?? new ResizeObserver(() => computeLayout());

      resizeObserver.disconnect();
      resizeObserver.observe(courtEl);
      resizeObserver.observe(hoopEl);
      if (overlayEl) resizeObserver.observe(overlayEl);
      roRef.current = resizeObserver;

      window.addEventListener("resize", computeLayout);
      window.addEventListener("scroll", computeLayout, true);

      return () => {
        recalcTimeouts.forEach((id) => window.clearTimeout(id));
        resizeObserver.disconnect();
        window.removeEventListener("resize", computeLayout);
        window.removeEventListener("scroll", computeLayout, true);
      };
    }, [
      ballElement,
      courtRef,
      isShotEvent,
      sx,
      sy,
      targetHoopRef,
      shotEventKey,
    ]);

    // Curve tuning: 3P has a higher arch, 2P medium, FT Scored lighter
    const ctrl = useMemo(() => {
      if (!layout) return null;
      const { start, end, viewH } = layout;
      const midX = (start.x + end.x) / 2;
      const baseY = Math.min(start.y, end.y);
      const archFactor = isThreePointCode(nsc)
        ? 0.26
        : isTwoPointCode(nsc)
        ? 0.18
        : 0.12;
      const ctrlY = Math.min(
        baseY - archFactor * viewH,
        Math.min(start.y, end.y) - viewH * 0.05
      );
      return {
        x: midX,
        y: Math.max(0, ctrlY),
      };
    }, [layout, nsc]);

    const pathD = useMemo(() => {
      if (!layout || !ctrl) return null;
      const {
        start: { x: Sx, y: Sy },
        end: { x: Ex, y: Ey },
      } = layout;
      const { x: Cx, y: Cy } = ctrl;
      return `M ${Sx},${Sy} Q ${Cx},${Cy} ${Ex},${Ey}`;
    }, [ctrl, layout]);

    const pathRef = useRef<SVGPathElement | null>(null);
    const [, setPlaying] = useState(false);
    const animationSignatureRef = useRef<string | null>(null);
    const animationStartedRef = useRef(false);
    const fallbackCompletedRef = useRef(false);
    const fadeOutTimeoutRef = useRef<number | null>(null);
    const layoutKey = useMemo(
      () =>
        layout
          ? [
              layout.viewW,
              layout.viewH,
              layout.start.x,
              layout.start.y,
              layout.end.x,
              layout.end.y,
            ].join("|")
          : "",
      [layout]
    );

    useEffect(() => {
      animationSignatureRef.current = null;
      animationStartedRef.current = false;
      fallbackCompletedRef.current = false;
      setVisible(false);
    }, [shotEventKey]);

    // trigger when a qualifying shot event arrives
    useEffect(() => {
      if (!isShotEvent || duration <= 0 || !layout || !layoutKey) {
        setPlaying(false);
        setVisible(false);
        if (!isShotEvent) animationSignatureRef.current = null;
        return;
      }
      const signature = `${shotEventKey}|${layoutKey}`;
      if (animationSignatureRef.current === signature) return;
      animationSignatureRef.current = signature;

      setPlaying(true);
      setVisible(true);
      onStartRef.current?.();
      if (fadeOutTimeoutRef.current) {
        window.clearTimeout(fadeOutTimeoutRef.current);
        fadeOutTimeoutRef.current = null;
      }

      let rafId: number | null = null;
      let startDelayId: number | null = null;

      const startAnimation = () => {
        const el = pathRef.current;
        if (!el) return false;
        animationStartedRef.current = true;

        const len = el.getTotalLength();
        el.style.strokeDasharray = `${len}`;
        el.style.strokeDashoffset = `${len}`;

        const startTs = performance.now();
        const step = (now: number) => {
          const t = Math.min(1, (now - startTs) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          const off = len * (1 - eased);
          el.style.strokeDashoffset = `${off}`;

          if (t < 1) {
            rafId = requestAnimationFrame(step);
          } else {
            setPlaying(false);
            fallbackCompletedRef.current = true;
            onEndRef.current?.();
            if (SHOT_PATH_FADE_OUT_DELAY_MS <= 0) {
              setVisible(false);
            } else {
              fadeOutTimeoutRef.current = window.setTimeout(() => {
                fadeOutTimeoutRef.current = null;
                setVisible(false);
              }, SHOT_PATH_FADE_OUT_DELAY_MS);
            }
          }
        };

        rafId = requestAnimationFrame(step);
        return true;
      };

      if (!startAnimation()) {
        startDelayId = requestAnimationFrame(() => {
          if (!startAnimation()) {
            setPlaying(false);
            if (fadeOutTimeoutRef.current) {
              window.clearTimeout(fadeOutTimeoutRef.current);
              fadeOutTimeoutRef.current = null;
            }
            setVisible(false);
            animationSignatureRef.current = null;
            fallbackCompletedRef.current = true;
            onEndRef.current?.();
          }
        });
      }

      return () => {
        if (rafId) cancelAnimationFrame(rafId);
        if (startDelayId) cancelAnimationFrame(startDelayId);
        if (animationSignatureRef.current === signature) {
          animationSignatureRef.current = null;
          animationStartedRef.current = false;
        }
        if (fadeOutTimeoutRef.current) {
          window.clearTimeout(fadeOutTimeoutRef.current);
          fadeOutTimeoutRef.current = null;
        }
        setVisible(false);
      };
    }, [duration, isShotEvent, layout, layoutKey, nsc, shotEventKey]);

    useEffect(() => {
      if (!isShotEvent) return;
      if (duration > 0 && layout && layoutKey) return;

      const timeout = window.setTimeout(() => {
        if (!animationStartedRef.current && !fallbackCompletedRef.current) {
          fallbackCompletedRef.current = true;
          setPlaying(false);
          if (fadeOutTimeoutRef.current) {
            window.clearTimeout(fadeOutTimeoutRef.current);
            fadeOutTimeoutRef.current = null;
          }
          setVisible(false);
          onEndRef.current?.();
        }
      }, duration <= 0 ? 0 : 220);

      return () => window.clearTimeout(timeout);
    }, [duration, isShotEvent, layout, layoutKey, shotEventKey]);

    if (!isShotEvent || !layout || !pathD) return null;

    return (
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 1200,
          opacity: visible ? 1 : 0,
          transition: visible
            ? "opacity 40ms ease-out"
            : "opacity 120ms ease-in",
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${layout.viewW} ${layout.viewH}`}
          preserveAspectRatio="none"
        >
          <>
            <path
              d={pathD}
              fill="none"
              stroke="#fff"
              strokeWidth="8"
              strokeLinecap="round"
              opacity={0.85}
              filter="drop-shadow(0 0 4px rgba(255,255,255,0.9))"
            />
            <path
              ref={pathRef}
              d={pathD}
              fill="none"
              stroke={strokeColor}
              strokeWidth="4"
              strokeLinecap="round"
              opacity={1}
              filter="drop-shadow(0 0 5px rgba(255,255,255,0.8))"
            />
          </>
        </svg>
      </div>
    );
  }
);
